import { formatCurrencyFromCents } from '@/utils/currencyHelpers'

const ROUND_UP_INCREMENT_IN_CENTS = 10000

/** The increment release rounds up to, for copy that has to name it. */
export const ROUND_UP_INCREMENT_LABEL = formatCurrencyFromCents(
  ROUND_UP_INCREMENT_IN_CENTS
).replace(/\.00$/, '')

export interface EstimateDisplayReconciliation {
  displayInCents: number | null
  roundingDeltaInCents: number | null
  /**
   * The figure the rounding was applied to. Present only alongside a rounding
   * delta, so the page can show what the delta is the difference between
   * rather than an unexplained amount.
   */
  preRoundingInCents: number | null
  /**
   * The released amount doesn't match the calculation and the gap is too large
   * to be release rounding. An ops adjustment and a calculation re-run since
   * release both land here, so callers must not attribute it to either.
   */
  releasedAmountDiverges: boolean
}

/**
 * Decides which amount the breakdown headlines and how to explain any gap
 * between the calculator's raw figure and the released estimate the customer
 * is asked to pay. Release rounds up to the nearest $100, so a gap inside
 * that increment is rounding; anything else means the two figures diverged
 * after release and must not be labeled as rounding.
 */
export const reconcileEstimateDisplay = ({
  calcAmountInCents,
  releasedAmountInCents,
}: {
  calcAmountInCents: number | null | undefined
  releasedAmountInCents: number | null | undefined
}): EstimateDisplayReconciliation => {
  if (releasedAmountInCents === null || releasedAmountInCents === undefined) {
    return {
      displayInCents: calcAmountInCents ?? null,
      roundingDeltaInCents: null,
      preRoundingInCents: null,
      releasedAmountDiverges: false,
    }
  }

  if (calcAmountInCents === null || calcAmountInCents === undefined) {
    return {
      displayInCents: releasedAmountInCents,
      roundingDeltaInCents: null,
      preRoundingInCents: null,
      releasedAmountDiverges: false,
    }
  }

  const delta = releasedAmountInCents - Math.max(calcAmountInCents, 0)

  if (delta > 0 && delta < ROUND_UP_INCREMENT_IN_CENTS) {
    return {
      displayInCents: releasedAmountInCents,
      roundingDeltaInCents: delta,
      preRoundingInCents: Math.max(calcAmountInCents, 0),
      releasedAmountDiverges: false,
    }
  }

  return {
    displayInCents: releasedAmountInCents,
    roundingDeltaInCents: null,
    preRoundingInCents: null,
    releasedAmountDiverges: delta !== 0,
  }
}
